<script lang="ts">
  /**
   * Container for Lead Collector modules
   */

  import { onMount, getContext } from "svelte"
  import ModuleTypes from "../constants/ModuleTypes"
  import IframeModuleContainer from "./IframeModuleContainer.svelte"
  import Loading from "./Loading.svelte"
  import { customThemeForModuleType } from "../lib/Helpers"
  import type { DealerTheme } from "conversion_flows/shared/contexts/dealerTheme"
  import type { Theme } from "website_script/types/theme.ts"
  import type { BuildUrlDataOutput } from "website_script/lib/buildModuleUrlData"
  import type ConversionFlow from "../../conversion_flows/ConversionFlow.svelte"
  import type { WebsiteScriptData } from "conversion_flows/types/websiteScriptData"

  const MODULE_TYPES_WITH_LAZY_LOADED_COMPONENT_FLOWS: {
    [K in ModuleTypes]?: string
  } = {
    [ModuleTypes.BOOK_TEST_DRIVE]: "book_test_drive",
    [ModuleTypes.PRICE_ALERT]: "price_alert",
    [ModuleTypes.CUSTOM]: "custom",
  }
  const dealerTheme = getContext<DealerTheme>("dealerTheme")

  /** the url for making the request to the server */
  export let url: string | null
  /** data for making the request to the server */
  export let moduleData: BuildUrlDataOutput
  /** the module type (used to determine flow component) */
  export let moduleType: ModuleTypes
  /** locale for the module */
  export let locale: string
  /** will the module be displayed inline (or in a modal) */
  export let displayInline: boolean = false

  let loaded: boolean = false
  let loadedComponent: typeof ConversionFlow

  $: lazyLoadedComponentFlow =
    MODULE_TYPES_WITH_LAZY_LOADED_COMPONENT_FLOWS[moduleType]

  let customTheme: Partial<Theme> | null
  $: customTheme = customThemeForModuleType(moduleType, dealerTheme)

  $: inlineStyles =
    displayInline && customTheme?.containerMaxWidth
      ? [`max-width: ${customTheme.containerMaxWidth}`, `margin: 0 auto`].join(
          ";"
        )
      : ""

  /**
   * Lazy loads the ConversionFlow component
   * @returns {Promise<Module>} the loaded module
   */
  const loadConversionFlow = () =>
    import("../../conversion_flows/ConversionFlow.svelte")

  onMount(() => {
    if (lazyLoadedComponentFlow) {
      // Load the lazy component
      loadConversionFlow().then((module) => {
        loaded = true
        loadedComponent = module.default
      })
    }
  })

  const handleLoaded = () => {
    loaded = true
  }

  const websiteScriptData: WebsiteScriptData = {
    cId: moduleData.cId,
    carUrl: moduleData.refUrl,
    depId: moduleData.depId ?? undefined,
    leadFlowId: moduleData.leadFlowId?.toString() ?? undefined,
    locale: locale,
    dataFromSource: moduleData.data_from_source,
    appHost: moduleData.host,
  }
</script>

{#if !loaded}
  <!-- Display a spinner while we are loading the content -->
  <div class="auws-loading-container">
    <Loading color={dealerTheme.brandPrimary} />
  </div>
{/if}

{#if lazyLoadedComponentFlow}
  {#if loadedComponent}
    <div style={inlineStyles}>
      <svelte:component
        this={loadedComponent}
        flow={lazyLoadedComponentFlow}
        {moduleType}
        {websiteScriptData}
        on:flowEvent
      />
    </div>
  {/if}
{:else if url}
  <IframeModuleContainer
    {url}
    {websiteScriptData}
    {moduleType}
    carId={moduleData.au_car_id}
    on:load={handleLoaded}
    on:flowEvent
    on:viewChanged
  />
{/if}

<style>
  .auws-loading-container {
    width: 100%;
    display: flex;
    min-height: 100px;
    justify-content: center;
    align-items: center;
  }
</style>
